import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import { useAuth } from "@/context/AuthContext";
import * as n from "@/api/nostalgia";

// ═══════════════════════════════════════════════════════════════════
// Memories — the library as a set of CUES.
//
// Nostalgia is triggered, not summoned. Nobody opens a blank box and
// remembers something, so the unit here is a shared cue — a Rasna ad, a
// Doordarshan title card — that people tap "I remember this" on. Only some of
// them then write, and that asymmetry is the design, not a funnel to fix.
//
// Three things this page exists to make visible:
//
//   · TWO counts, deliberately far apart. `rememberCount` is the room
//     agreeing; `writtenCount` is the few who had words. The gap is the point.
//   · WHO tapped is never returned. The count is public, the tap is private —
//     so there is no list of rememberers to render, and that is not missing data.
//   · `iRemember` is NULLABLE. Null means "you are not signed in, so we do not
//     know", which is not "you have not". Rendering null as an untapped button
//     would tell a signed-out reader something we cannot actually see.
//
// Writing is a PUT: one memory per person per cue, so writing again replaces
// rather than piling up.
// ═══════════════════════════════════════════════════════════════════

export default function MemoriesLab() {
  const [category, setCategory] = useState<string | null>(null);
  const [cueId, setCueId] = useState<string | null>(null);

  const { data: cats, isLoading: loadingCats } = useQuery({
    queryKey: ["library-categories"],
    queryFn: n.getLibraryCategories,
  });

  const { data: items } = useQuery({
    queryKey: ["library-by-category", category],
    queryFn: () => n.getLibraryByCategory(category!),
    enabled: !!category,
  });

  const categories = cats?.data.categories ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Memories"
        subtitle="The library as cues. Tap remember; write only if you have words."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Nostalgia", href: "/lab/nostalgia" },
          { label: "Memories" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-2">Categories</h2>
        {loadingCats ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !categories.length ? (
          <p className="text-sm text-gray-500">The catalogue is empty.</p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c.category}
                onClick={() => {
                  setCategory(c.category);
                  setCueId(null);
                }}
                className={`px-3 py-1.5 rounded-full text-sm ${
                  category === c.category
                    ? "bg-gray-900 text-white"
                    : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {c.category.replace(/_/g, " ")} · {c.count}
              </button>
            ))}
          </div>
        )}
      </section>

      {category && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-gray-900 mb-3">
            {category.replace(/_/g, " ")}
          </h2>
          {!items ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !items.data.items.length ? (
            <p className="text-sm text-gray-500">Nothing catalogued here yet.</p>
          ) : (
            <div className="flex gap-2 flex-wrap">
              {items.data.items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => setCueId(it.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    cueId === it.id
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {it.name}
                  {it.era && <span className="text-gray-400 text-xs"> · {it.era}</span>}
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {cueId && <Cue id={cueId} />}
    </div>
  );
}

function Cue({ id }: { id: string }) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [body, setBody] = useState("");
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["library-item", id],
    queryFn: () => n.getLibraryItem(id),
  });

  // The paged list, separate from the handful the detail already embeds.
  const { data: all } = useQuery({
    queryKey: ["library-item-memories", id],
    queryFn: () => n.listItemMemories(id, { limit: 20, offset: 0 }),
    enabled: showAll,
  });

  const d = data?.data;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["library-item", id] });
      await qc.invalidateQueries({ queryKey: ["library-item-memories", id] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!d) return <p className="text-sm text-gray-500">Not found.</p>;

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{d.item.name}</h2>
        <div className="flex gap-2 flex-wrap mt-1">
          <Badge variant="gray">{d.item.category.replace(/_/g, " ")}</Badge>
          {d.item.era && <Badge variant="blue">{d.item.era}</Badge>}
          {d.item.tags.map((t) => (
            <Badge key={t} variant="gray">
              {t}
            </Badge>
          ))}
        </div>
        {d.item.description && (
          <p className="text-sm text-gray-700 mt-2">{d.item.description}</p>
        )}
      </div>

      <div className="flex gap-6 items-end">
        <div>
          <div className="text-2xl font-semibold text-gray-900 tabular-nums">
            {d.rememberCount}
          </div>
          <div className="text-xs text-gray-500">remember this</div>
        </div>
        <div>
          <div className="text-2xl font-semibold text-gray-900 tabular-nums">
            {d.writtenCount}
          </div>
          <div className="text-xs text-gray-500">had words for it</div>
        </div>
        <p className="text-xs text-gray-400 pb-1">
          Who tapped is never returned — the count is public, the tap is private.
        </p>
      </div>

      {/* iRemember === null is "not signed in", NOT "has not". */}
      {d.iRemember === null ? (
        <p className="text-sm text-gray-500">
          Sign in to say whether you remember this — signed out, we genuinely do
          not know, which is not the same as no.
        </p>
      ) : (
        <div className="flex gap-2 items-center">
          <Button
            size="sm"
            variant={d.iRemember ? "primary" : "secondary"}
            isLoading={busy}
            onClick={() => run(() => (d.iRemember ? n.forgetItem(id) : n.rememberItem(id)))}
          >
            {d.iRemember ? "I remember this ✓" : "I remember this"}
          </Button>
          {d.iRemember && (
            <span className="text-xs text-gray-500">tap again to take it back</span>
          )}
        </div>
      )}

      {isAuthenticated && (
        <div className="border-t border-gray-200 pt-4">
          {/*
            Tapping remember CREATES the row, so `myMemory` is non-null with an
            empty body the moment you tap. Branching on the row makes the page
            claim "Your memory" over nothing; the question that matters is
            whether they WROTE, which is `body`.
          */}
          <p className="text-sm font-medium text-gray-900">
            {d.myMemory?.body ? "Your memory" : "Write one, only if you have words"}
          </p>
          {d.myMemory?.body && (
            <p className="text-sm text-gray-700 mt-1 mb-2 whitespace-pre-wrap border-l-2 border-gray-200 pl-3">
              {d.myMemory.body}
            </p>
          )}
          <TextAreaField
            label=""
            rows={3}
            placeholder={d.myMemory?.body ? "Replace it…" : "What it brings back…"}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <Button
              size="sm"
              isLoading={busy}
              disabled={body.trim().length < 1}
              onClick={() =>
                run(async () => {
                  // PUT — writing again replaces, it does not pile up.
                  await n.writeMemory(id, { body: body.trim(), isAnonymous: anon });
                  setBody("");
                })
              }
            >
              {d.myMemory?.body ? "Replace mine" : "Write mine"}
            </Button>
            <label className="text-sm text-gray-600 flex items-center gap-1.5">
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} />
              anonymously
            </label>
            {d.myMemory?.body && (
              <button
                disabled={busy}
                onClick={() => run(() => n.clearMemory(id))}
                className="text-xs text-red-700 disabled:opacity-50"
              >
                clear mine
              </button>
            )}
          </div>
          {err && <p className="text-sm text-red-700 mt-2">{err}</p>}
        </div>
      )}

      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-900">
            {showAll ? "All memories" : "Recent memories"}
          </p>
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-blue-700 underline"
          >
            {showAll ? "show the recent few" : "load the full list"}
          </button>
        </div>
        <MemoryList
          memories={showAll ? (all?.data.memories ?? []) : d.recentMemories}
          total={showAll ? all?.data.meta.total : undefined}
        />
      </div>
    </section>
  );
}

function MemoryList({
  memories,
  total,
}: {
  memories: n.NostalgiaMemory[];
  total?: number;
}) {
  if (!memories.length)
    return <p className="text-sm text-gray-500">Nobody has written one yet.</p>;

  return (
    <>
      {total !== undefined && (
        <p className="text-xs text-gray-500 mb-2">{total} in total</p>
      )}
      <ul className="space-y-2">
        {memories.map((m) => (
          <li key={m.id} className="border border-gray-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2">
              {/* author is null on an anonymous memory — REMOVED, not hidden. */}
              <span className="font-medium text-gray-900 text-sm">
                {m.author?.name ?? "Anonymous"}
              </span>
              {m.author?.city && (
                <span className="text-xs text-gray-400">{m.author.city}</span>
              )}
              <span className="text-xs text-gray-400">
                {new Date(m.createdAt).toLocaleDateString()}
              </span>
            </div>
            {m.body && (
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{m.body}</p>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
